/*
 * Copyright (c) 2023-2024 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { Form, FormInstance, Input } from 'antd'
import { InternalNamePath, NamePath } from 'antd/es/form/interface'
import { ReactNode } from 'react'
import { FormInputLabel } from '../FormInputLabel/FormInputLabel'
import { RequirementChecklistPopover } from '../RequirementChecklistPopover/RequirementChecklistPopover'

const LABELS = {
  password: 'Enter password',
  confirmPassword: 'Re-enter password'
}
const PASSWORD_POPOVER_ID = 'passwordpopover'

export interface PasswordForm {
  password: string
  confirmPassword: string
}

interface Props {
  formPrefix?: InternalNamePath
  layout: 'horizontal' | 'vertical'
  children: (props: { passwordElement: JSX.Element; confirmPasswordElement: JSX.Element }) => JSX.Element
  labels?: typeof LABELS
}

export function CreateNewPassword({ formPrefix, layout, labels = LABELS, children }: Props) {
  const isLayoutHorizontal = layout === 'horizontal'

  const formInstance = Form.useFormInstance<PasswordForm>()
  const passwordElementValue = Form.useWatch(getMergedNamePath(formPrefix, 'password'), formInstance)

  const passwordElement = (
    <PasswordPopover formInstance={formInstance}>
      <Form.Item
        name="password"
        label={<FormInputLabel layout={layout}>{labels.password}</FormInputLabel>}
        colon={!isLayoutHorizontal}
        validateTrigger="onBlur"
        aria-describedby={PASSWORD_POPOVER_ID}
        rules={[
          {
            required: true,
            validator: (_, value) => {
              if (!value) {
                return Promise.reject(new Error('Password is required.'))
              }

              if (!PasswordValidatorHelpers.isBetween8And32Characters(value)) {
                return Promise.reject(new Error('Password must be between 8 and 32 characters.'))
              }

              if (!PasswordValidatorHelpers.isContain3OrMoreRequiredCharTypes(value)) {
                return Promise.reject(new Error('Password does not meet the minimum requirements.'))
              }

              if (PasswordValidatorHelpers.isContainMoreThanTwoConsecutiveCharacters(value)) {
                return Promise.reject(new Error('Password must not contain more than 2 equal characters in a row.'))
              }

              return Promise.resolve()
            }
          }
        ]}>
        <Input.Password placeholder={labels.password} autoComplete="new-password" />
      </Form.Item>
    </PasswordPopover>
  )

  const confirmPasswordElement = (
    <Form.Item
      name="confirmPassword"
      label={<FormInputLabel layout={layout}>{labels.confirmPassword}</FormInputLabel>}
      colon={!isLayoutHorizontal}
      validateTrigger="onBlur"
      rules={[
        {
          required: true,
          validator: (_, value) => {
            if (!value) {
              return Promise.reject(new Error('Confirmation password is required.'))
            }

            if (value !== passwordElementValue) {
              return Promise.reject(new Error('Confirmation password must be the same as the password.'))
            }

            return Promise.resolve()
          }
        }
      ]}>
      <Input.Password placeholder={labels.confirmPassword} autoComplete="new-password" />
    </Form.Item>
  )

  return children({ passwordElement, confirmPasswordElement })
}

// Helper components/functions.
// TODO: this is duplicated from InputTags, we will need to refactor it later to utils in `@cp/shared-ui`.
function getMergedNamePath(prefix: InternalNamePath | undefined, name: NamePath | undefined) {
  if (!name) return []
  return (prefix ?? []).concat(name)
}

function PasswordPopover({ children, formInstance }: { children: ReactNode; formInstance: FormInstance<PasswordForm> }) {
  const passwordValue = Form.useWatch('password', formInstance) || ''

  return (
    <RequirementChecklistPopover
      id={PASSWORD_POPOVER_ID}
      headerText="The password requirements are as follows:"
      fieldValue={passwordValue}
      requirements={[
        {
          description: 'Contains between 8 to 32 characters.',
          validator: PasswordValidatorHelpers.isBetween8And32Characters
        },
        {
          description: 'Contains at least 3 of following 4 types of characters:',
          minPassedChildValidator: 3,
          validator: [
            {
              childDescription: 'Uppercase letters',
              childValidator: PasswordValidatorHelpers.isContainUppercase
            },
            {
              childDescription: 'Lowercase letters',
              childValidator: PasswordValidatorHelpers.isContainLowercase
            },
            {
              childDescription: 'Numbers',
              childValidator: PasswordValidatorHelpers.isContainNumber
            },
            {
              childDescription: 'Special characters',
              childValidator: PasswordValidatorHelpers.isContainSpecialCharacter
            }
          ]
        },
        {
          description: 'Does not contain more than 2 equal characters in a row.',
          validator: value => !PasswordValidatorHelpers.isContainMoreThanTwoConsecutiveCharacters(value)
        }
      ]}>
      {children}
    </RequirementChecklistPopover>
  )
}

class PasswordValidatorHelpers {
  static isContainUppercase = (password: string) => /[A-Z]/.test(password)
  static isContainLowercase = (password: string) => /[a-z]/.test(password)
  static isContainNumber = (password: string) => /\d/.test(password)
  static isContainSpecialCharacter = (password: string) => /[^a-zA-Z0-9]/.test(password)
  static isContainMoreThanTwoConsecutiveCharacters = (password: string) => /(.)\1{2,}/.test(password)
  static isBetween8And32Characters = (password: string) => {
    const length = password.length

    return length >= 8 && length <= 32
  }

  static isContain3OrMoreRequiredCharTypes = (password: string) => {
    const validations = {
      isContainUppercase: PasswordValidatorHelpers.isContainUppercase(password),
      isContainLowercase: PasswordValidatorHelpers.isContainLowercase(password),
      isContainNumber: PasswordValidatorHelpers.isContainNumber(password),
      isContainSpecialCharacter: PasswordValidatorHelpers.isContainSpecialCharacter(password)
    }

    return Object.values(validations).filter(Boolean).length >= 3
  }
}
